import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    IconButton,
    List,
    ListItemButton,
    TextField,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import _ from 'lodash';
import { api } from '../../configurations/api';
import { getMyProjectsUrl } from '../../configurations/api/url';
import { httpRequest } from '../../redux/types';
import type { IProject } from '../../types/global/helper';
import { OBSIDIAN, OBSIDIAN_BODY_FONT, OBSIDIAN_DISPLAY_FONT } from '../../constants/obsidianDashboardTheme';
import { resolveSandboxMediaUrl } from '../../utils/sandboxCourseApi';

type Props = {
    open: boolean;
    onClose: () => void;
    onSelect: (project: IProject) => void;
    /** Optional: open create/upload flow from empty state or header */
    onUploadNew?: () => void;
    title?: string;
};

function unwrapProjects(raw: any): IProject[] {
    const data = raw?.data?.data ?? raw?.data ?? raw;
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.projects)) return data.projects;
    if (Array.isArray(data?.docs)) return data.docs;
    return [];
}

/** Pick a private video project (same signed-upload media used for class HLS). */
export function SandboxPrivateProjectPicker({
    open,
    onClose,
    onSelect,
    onUploadNew,
    title = 'Pick a private project',
}: Props) {
    const [search, setSearch] = useState('');
    const [debounced, setDebounced] = useState('');
    const [loading, setLoading] = useState(false);
    const [projects, setProjects] = useState<IProject[]>([]);
    const abortRef = useRef(0);

    const debouncedSet = useMemo(
        () =>
            _.debounce((v: string) => {
                setDebounced(v.trim());
            }, 350),
        [],
    );

    useEffect(() => {
        debouncedSet(search);
        return () => debouncedSet.cancel();
    }, [search, debouncedSet]);

    const fetchProjects = useCallback(async () => {
        if (!open) return;
        const reqId = ++abortRef.current;
        setLoading(true);
        try {
            const qs = new URLSearchParams({
                page: '1',
                limit: '40',
                type: 'video',
                public_status: 'private',
            });
            if (debounced.length >= 2) qs.set('searchTerm', debounced);
            const res = await api(`${getMyProjectsUrl}?${qs}`, httpRequest.GET, null, 2);
            if (reqId !== abortRef.current) return;
            const list = unwrapProjects(res).filter(p => !p.public_status || p.public_status === 'private');
            setProjects(list);
        } catch {
            if (reqId === abortRef.current) setProjects([]);
        } finally {
            if (reqId === abortRef.current) setLoading(false);
        }
    }, [open, debounced]);

    useEffect(() => {
        if (!open) {
            setSearch('');
            setDebounced('');
            setProjects([]);
            return;
        }
        fetchProjects();
    }, [open, fetchProjects]);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="sm"
            PaperProps={{
                sx: {
                    bgcolor: OBSIDIAN.surfaceContainerHigh,
                    backgroundImage: 'none',
                    borderRadius: 3,
                    border: `1px solid ${OBSIDIAN.outline}44`,
                },
            }}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 2.5,
                    py: 2,
                    borderBottom: `1px solid ${OBSIDIAN.outline}33`,
                }}>
                <Box>
                    <Typography sx={{ fontFamily: OBSIDIAN_DISPLAY_FONT, fontWeight: 700, color: OBSIDIAN.onSurface }}>
                        {title}
                    </Typography>
                    <Typography
                        sx={{
                            fontFamily: OBSIDIAN_BODY_FONT,
                            fontSize: 12,
                            color: OBSIDIAN.onSurfaceVariant,
                            mt: 0.25,
                        }}>
                        Only your private projects (HLS) appear here
                    </Typography>
                </Box>
                <IconButton onClick={onClose} size="small" sx={{ color: OBSIDIAN.onSurfaceVariant }}>
                    <CloseIcon />
                </IconButton>
            </Box>
            <Box sx={{ p: 2 }}>
                {onUploadNew ? (
                    <Button
                        fullWidth
                        startIcon={<CloudUploadOutlinedIcon />}
                        onClick={onUploadNew}
                        sx={{
                            mb: 1.5,
                            textTransform: 'none',
                            fontFamily: OBSIDIAN_BODY_FONT,
                            fontWeight: 700,
                            borderRadius: 2,
                            bgcolor: OBSIDIAN.primary,
                            color: OBSIDIAN.onPrimaryFixed,
                            '&:hover': { filter: 'brightness(1.05)', bgcolor: OBSIDIAN.primary },
                        }}>
                        Upload a new private project
                    </Button>
                ) : null}
                <TextField
                    fullWidth
                    placeholder="Search private projects…"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    InputProps={{
                        startAdornment: <SearchIcon sx={{ color: OBSIDIAN.onSurfaceVariant, mr: 1 }} />,
                        endAdornment: loading ? <CircularProgress size={16} sx={{ color: OBSIDIAN.primary }} /> : null,
                    }}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            bgcolor: '#323232',
                            color: OBSIDIAN.onSurface,
                            borderRadius: 2,
                            '& fieldset': { borderColor: `${OBSIDIAN.outline}66` },
                            '&:hover fieldset': { borderColor: OBSIDIAN.outline },
                            '&.Mui-focused fieldset': { borderColor: OBSIDIAN.primary },
                        },
                        '& .MuiInputBase-input::placeholder': {
                            color: OBSIDIAN.onSurfaceVariant,
                            opacity: 1,
                        },
                    }}
                />
                <List dense sx={{ mt: 1, maxHeight: 360, overflowY: 'auto' }}>
                    {!loading && projects.length === 0 ? (
                        <Box sx={{ py: 4, textAlign: 'center' }}>
                            <LockOutlinedIcon sx={{ color: OBSIDIAN.onSurfaceVariant, mb: 1 }} />
                            <Typography
                                sx={{ color: OBSIDIAN.onSurfaceVariant, fontSize: 13, fontFamily: OBSIDIAN_BODY_FONT }}>
                                No private projects found.
                            </Typography>
                            {onUploadNew ? (
                                <Button
                                    onClick={onUploadNew}
                                    sx={{
                                        mt: 1.5,
                                        textTransform: 'none',
                                        color: OBSIDIAN.primary,
                                        fontFamily: OBSIDIAN_BODY_FONT,
                                        fontWeight: 700,
                                    }}>
                                    Upload one now
                                </Button>
                            ) : (
                                <Typography sx={{ color: OBSIDIAN.onSurfaceVariant, fontSize: 12, mt: 0.5 }}>
                                    Upload a private video first.
                                </Typography>
                            )}
                        </Box>
                    ) : (
                        projects.map(p => {
                            const thumb = resolveSandboxMediaUrl(p.feature_image || p.thumbnail) || undefined;
                            return (
                                <ListItemButton
                                    key={p._id}
                                    onClick={() => {
                                        onSelect(p);
                                        onClose();
                                    }}
                                    sx={{
                                        gap: 1.5,
                                        py: 1.25,
                                        borderRadius: 1.5,
                                        '&:hover': { bgcolor: OBSIDIAN.surfaceContainerHighest },
                                    }}>
                                    <Box
                                        component="img"
                                        src={thumb}
                                        alt=""
                                        sx={{
                                            width: 56,
                                            height: 56,
                                            borderRadius: 1,
                                            objectFit: 'cover',
                                            bgcolor: '#323232',
                                            flexShrink: 0,
                                        }}
                                    />
                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                        <Typography
                                            noWrap
                                            sx={{
                                                fontFamily: OBSIDIAN_BODY_FONT,
                                                fontWeight: 600,
                                                color: OBSIDIAN.onSurface,
                                                fontSize: 14,
                                            }}>
                                            {p.title || 'Untitled'}
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontSize: 11,
                                                color: OBSIDIAN.secondary,
                                                fontFamily: OBSIDIAN_BODY_FONT,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 0.5,
                                            }}>
                                            <LockOutlinedIcon sx={{ fontSize: 12 }} /> Private
                                            {p.processing_complete === false ? ' · Processing' : ''}
                                        </Typography>
                                    </Box>
                                </ListItemButton>
                            );
                        })
                    )}
                </List>
            </Box>
        </Dialog>
    );
}
